import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { SnoozeDays } from "@/pages/tina/site-health/hooks/use-site-health-repository";

const CHOICES: SnoozeDays[] = [3, 7, 30];

interface SnoozeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** `false` means "did not fully succeed" — the dialog stays open, note kept, so nothing typed is lost. */
  onConfirm: (days: SnoozeDays, note: string | null) => Promise<boolean>;
}

export function SnoozeDialog({ open, onOpenChange, title, onConfirm }: SnoozeDialogProps) {
  const [days, setDays] = useState<SnoozeDays>(7);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  async function confirm() {
    setBusy(true);
    try {
      const ok = await onConfirm(days, note.trim() || null);
      if (ok) {
        setNote("");
        onOpenChange(false);
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Snooze {title}</DialogTitle>
          <DialogDescription>
            It comes back after this, or sooner if it turns red.
          </DialogDescription>
        </DialogHeader>
        <div className="flex gap-2">
          {CHOICES.map((d) => (
            <Button
              key={d}
              type="button"
              size="sm"
              variant={d === days ? "default" : "outline"}
              aria-pressed={d === days}
              onClick={() => setDays(d)}
            >
              {d} days
            </Button>
          ))}
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="snooze-note">Note</Label>
          <Textarea
            id="snooze-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Why (optional)"
          />
        </div>
        <DialogFooter>
          <Button type="button" disabled={busy} onClick={() => void confirm()}>
            Snooze
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
